import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/Card";
import dayjs from "dayjs";
import { fetchStages } from "../../services/leadStageService.js";

const COLOR_MAP = {
  blue: { bg: "bg-primary/20", text: "text-primary" },
  purple: { bg: "bg-purple-500/20", text: "text-purple-500" },
  sky: { bg: "bg-sky-500/20", text: "text-sky-500" },
  green: { bg: "bg-success/20", text: "text-success" },
  yellow: { bg: "bg-warning/20", text: "text-yellow-500" },
  red: { bg: "bg-destructive/20", text: "text-destructive" },
  pink: { bg: "bg-pink-500/20", text: "text-pink-500" },
  gray: { bg: "bg-gray-500/20", text: "text-gray-700" },
  indigo: { bg: "bg-blue-500/40", text: "text-blue-500" },
  orange: { bg: "bg-orange-500/20", text: "text-orange-500" },
  teal: { bg: "bg-teal-500/20", text: "text-teal-500" },
};

const DEFAULT_COLOR = { bg: "bg-gray-500/20", text: "text-gray-600" };

const DashboardStats = ({
  activeStatus,
  activeDealStatus,
  selectedDate,
  setActiveStatus,
  setActiveDealStatus,
  setSelectedDate,
  todayCount,
  handleStatusClick,
  // Individual counts (legacy — still passed from Dashboard)
  meetingCount,
  callbackCount,
  wonCount,
  prospectCount,
  lostCount,
  quotationCreatedCount,
  quotationsendCount,
  quotationReviseCount,
  // All deal status counts if available
  dealStatusCounts = {},
}) => {
  const today = dayjs().format("YYYY-MM-DD");
  const [stages, setStages] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchStages();
        setStages(res.data || []);
      } catch {
        setStages([]);
      }
    })();
  }, []);

  const handleTodayClick = () => {
    const isAlreadyToday =
      !activeStatus &&
      !activeDealStatus &&
      selectedDate?.format("YYYY-MM-DD") === today;
    if (isAlreadyToday) return;
    setActiveStatus("");
    setActiveDealStatus("");
    setSelectedDate(dayjs());
  };

  const forceStatusClick = (status) => {
    setActiveStatus("");
    setTimeout(() => {
      handleStatusClick(status);
    }, 0);
  };

  // Legacy count lookup for backward compat
  const legacyCounts = {
    new: todayCount,
    call_back: callbackCount,
    meeting: meetingCount,
    quotation_created: quotationCreatedCount,
    quotation_send: quotationsendCount,
    quotation_revised: quotationReviseCount,
    won: wonCount,
    lost: lostCount,
  };

  const getCount = (stageKey) => {
    // Try dealStatusCounts first (from stats API), then legacy props
    if (dealStatusCounts[stageKey] !== undefined) return dealStatusCounts[stageKey];
    if (legacyCounts[stageKey] !== undefined) return legacyCounts[stageKey];
    return 0;
  };

  // Filter dashboard-enabled stages, sorted by dashboard_order, max 9
  const dashboardStages = stages
    .filter((s) => s.show_on_dashboard)
    .sort((a, b) => (a.dashboard_order || 0) - (b.dashboard_order || 0))
    .slice(0, 9);

  // Find meeting count from stages or legacy
  const meetingStage = stages.find((s) => s.stage_key === "meeting");
  const meetingStageCount = getCount("meeting");

  // Build cards: 2 permanent + dynamic configurable stages
  const cards = [
    // PERMANENT: Today's Leads
    {
      label: "Today's Leads",
      count: todayCount,
      color: COLOR_MAP.blue || DEFAULT_COLOR,
      onClick: handleTodayClick,
    },
    // PERMANENT: Meeting Scheduled
    {
      label: meetingStage?.stage_label || "Meeting Scheduled",
      count: meetingStageCount,
      color: COLOR_MAP.sky || DEFAULT_COLOR,
      onClick: () => forceStatusClick("meeting"),
    },
    // DYNAMIC: configurable stages (exclude "new" and "meeting" — already shown above)
    ...dashboardStages
      .filter((s) => s.stage_key !== "new" && s.stage_key !== "meeting")
      .map((stage) => ({
        label: stage.stage_label,
        count: getCount(stage.stage_key),
        color: COLOR_MAP[stage.color] || DEFAULT_COLOR,
        onClick: () => forceStatusClick(stage.stage_key),
      })),
  ];

  // Fallback: if no stages loaded yet, show static cards
  if (stages.length === 0) {
    const fallbackCards = [
      { label: "Today's Leads", count: todayCount, color: COLOR_MAP.blue, onClick: handleTodayClick },
      { label: "Meeting Scheduled", count: meetingCount, color: COLOR_MAP.sky, onClick: () => forceStatusClick("meeting") },
      { label: "Call Back", count: callbackCount, color: COLOR_MAP.purple, onClick: () => forceStatusClick("call_back") },
      { label: "Won", count: wonCount, color: COLOR_MAP.green, onClick: () => forceStatusClick("won") },
      { label: "Lost", count: lostCount, color: COLOR_MAP.red, onClick: () => forceStatusClick("lost") },
    ];

    return (
      <div className="grid grid-cols-3 gap-2">
        {fallbackCards.map((card, index) => (
          <Card key={index} onClick={card.onClick} className="border-none shadow-sm cursor-pointer rounded-xl">
            <CardContent className={`p-2 rounded-xl backdrop-blur-xl ${card.color.bg}`}>
              <p className={`text-2xl font-bold ${card.color.text}`}>{card.count}</p>
              <p className="text-[9px] sm:text-[12px] text-muted-foreground text-nowrap">{card.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2">
      {cards.map((card, index) => (
        <Card
          key={index}
          onClick={card.onClick}
          className="border-none shadow-sm cursor-pointer rounded-xl"
        >
          <CardContent className={`p-2 rounded-xl backdrop-blur-xl ${card.color.bg}`}>
            <p className={`text-2xl font-bold ${card.color.text}`}>{card.count}</p>
            <p className="text-[9px] sm:text-[12px] text-muted-foreground text-nowrap">
              {card.label}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DashboardStats;
